import { MinMax } from 'common/table/types/TableV3';
import {
  isSupportedCalendarDate,
  isSupportedYears
} from 'common/table/utils/rangeFilterLimits';
import {
  convertBirthDateRangeToAgeRange,
  convertAgeRangeToBirthDateRange
} from 'modules/candidate-profile/tables/maps/convertAgeRange';
import {
  CandidateFilterValueType,
  CandidateRangeFilter,
  RangeFilterSettings
} from 'modules/candidate-profile/types/CandidateSearchFilter';

const minOrMaxToString = (value: number | string | undefined) =>
  value === undefined ? undefined : String(value);

const trimToCalendarDay = (value: number | string | undefined) =>
  minOrMaxToString(value)?.slice(0, 10);

const parseAsFiniteNumber = (value: string | undefined) => {
  const parsed = value === undefined || value === '' ? NaN : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const keepSupportedDate = (value: string | undefined) =>
  value !== undefined && isSupportedCalendarDate(value) ? value : undefined;

const parseAsSupportedNumber = (
  value: string | undefined,
  isYears: boolean
) => {
  const parsed = parseAsFiniteNumber(value);
  return parsed !== undefined && (!isYears || isSupportedYears(parsed))
    ? parsed
    : undefined;
};

const buildMinMax = <T>(min: T | undefined, max: T | undefined) => ({
  ...(min !== undefined && { min }),
  ...(max !== undefined && { max })
});

export const rangeConditionToColumnMinMax = (
  filter: CandidateRangeFilter,
  range: RangeFilterSettings | undefined
): MinMax => {
  if (filter.type === CandidateFilterValueType.NUMBER) {
    return buildMinMax(
      minOrMaxToString(filter.min),
      minOrMaxToString(filter.max)
    );
  }
  if (!range?.showsAge) {
    return buildMinMax(
      trimToCalendarDay(filter.min),
      trimToCalendarDay(filter.max)
    );
  }
  const ages = convertBirthDateRangeToAgeRange({
    min: minOrMaxToString(filter.min),
    max: minOrMaxToString(filter.max)
  });
  return buildMinMax(minOrMaxToString(ages.min), minOrMaxToString(ages.max));
};

export const columnMinMaxToRangeCondition = (
  minMax: MinMax,
  range: RangeFilterSettings | undefined
): MinMax<number | string> => {
  if (range?.kind === 'date')
    return buildMinMax(
      keepSupportedDate(minMax.min),
      keepSupportedDate(minMax.max)
    );
  const isYears = range?.kind === 'number' && range.unit === 'years';
  const numbers = buildMinMax(
    parseAsSupportedNumber(minMax.min, isYears),
    parseAsSupportedNumber(minMax.max, isYears)
  );
  return range?.showsAge ? convertAgeRangeToBirthDateRange(numbers) : numbers;
};
